/**
 * The dropdown beside the project name: go to another project without going
 * back to the register first.
 *
 * ── WHY IT IS NOT JUST THE BACK LINK ──────────────────────────────────────
 *
 * There is already a way to another project - Back, then find the row again -
 * and that is exactly the journey this replaces. The register is filtered,
 * sorted and paged, so "the project I was on ten minutes ago" is not reliably
 * on the first screen of it, and an operator comparing two jobs pays for the
 * search twice on every hop. The list here asks its own question and always
 * answers with the same short set.
 *
 * ── WHY THE SEARCH IS SERVER-SIDE ─────────────────────────────────────────
 *
 * `PickerField` is the app's search-in-a-menu control and it was the obvious
 * reuse, but it filters an array the caller already holds - which would mean
 * fetching every project on the tenant to open one menu. The register is paged
 * for a reason. So this panel keeps the same shape (a filter row over a list)
 * and sends the term to the endpoint instead, one page at a time.
 *
 * The panel is only mounted while the menu is open (`Pop` unmounts its
 * children), so the first fetch happens on the first opening and never on a
 * project page nobody switched away from.
 *
 * ── WHAT IT LEAVES OUT ────────────────────────────────────────────────────
 *
 * Archived projects, because the endpoint hides them by default and this is a
 * shortcut to work in progress - the same rule the pinned strip follows. The
 * project you are already on is dropped too: a row that navigates to the page
 * it is drawn on is a row that does nothing.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Loader2, Pin, Search } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getProjects, type Project } from "../../../lib/projects";
import { IconBtn } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";

/** Rows per opening. A menu is a shortcut, not a second register: past a dozen
 *  entries the scan costs more than the search box beside them. */
const LIMIT = 12;

/** The wait between the last keystroke and the request. Long enough that typing
 *  a client name is one fetch rather than nine. */
const DEBOUNCE_MS = 250;

/**
 * Keys the caret owns while the filter has focus.
 *
 * `Pop` uses these to walk the rows of a menu, which is right for a list of
 * buttons and wrong the moment one row is a text field - End would jump to the
 * last project instead of to the end of the word being typed. Up and Down stay
 * with `Pop` on purpose: stepping from the field into the results is what they
 * should do. Copied from `PickerField`, which has the same problem.
 */
const CARET_KEYS = new Set(["Home", "End", "ArrowLeft", "ArrowRight"]);

export function ProjectSwitcher({ project }: { project: Project }) {
  const t = useT();

  return (
    <Pop
      wide
      trigger={(open) => (
        <IconBtn
          small
          className={cn(open && "is-on")}
          title={t("projects.switch")}
          aria-label={t("projects.switch")}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </IconBtn>
      )}
    >
      <SwitcherPanel currentId={project.id} />
    </Pop>
  );
}

/** Mounted only while the menu is open, so every opening starts on the recent
 *  list rather than on whatever somebody searched for yesterday. */
function SwitcherPanel({ currentId }: { currentId: string }) {
  const t = useT();
  const nav = useNavigate();
  const close = useClosePops();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Project[] | null>(null);
  const [failed, setFailed] = useState(false);
  /** The first fetch is the opening itself and must not wait for a debounce
   *  nobody triggered. Every later one is a keystroke. */
  const first = useRef(true);

  useEffect(() => {
    let alive = true;
    const delay = first.current ? 0 : DEBOUNCE_MS;
    first.current = false;
    const timer = setTimeout(() => {
      getProjects({ q: q.trim() || undefined, pageSize: LIMIT, sort: "created", dir: "desc" })
        .then((res) => {
          if (!alive) return;
          setRows(res.rows);
          setFailed(false);
        })
        .catch(() => {
          if (!alive) return;
          // Said out loud rather than drawn as an empty list. "No projects
          // match" and "the request failed" look identical on screen and mean
          // opposite things, and the second one is the one worth retrying.
          setRows([]);
          setFailed(true);
        });
    }, delay);
    // `alive` guards the answer as well as the timer: a request already in
    // flight when the next keystroke lands would otherwise paint the older
    // term's rows over the newer one's.
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q]);

  const shown = useMemo(() => {
    const list = (rows ?? []).filter((p) => p.id !== currentId);
    // Pinned first, and the sort is stable, so inside each group the server's
    // order survives. The pin is this operator's own shortlist - the same rows
    // the strip over the register draws - so a menu that ignored it would be
    // the one place in the app where pinning buys nothing.
    return [...list].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
  }, [rows, currentId]);

  function go(id: string) {
    close();
    nav(`/projects/${id}`);
  }

  return (
    <>
      <label className="crm-msearch">
        <Search className="size-4 shrink-0 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (CARET_KEYS.has(e.key)) {
              e.stopPropagation();
              return;
            }
            // Enter takes the obvious answer and only the obvious one: the
            // single row left. Guessing between six matches would be a lottery
            // that navigates away from the page the operator is reading.
            if (e.key !== "Enter") return;
            e.preventDefault();
            const only = shown.length === 1 ? shown[0] : null;
            if (only) go(only.id);
          }}
          placeholder={t("projects.switchSearch")}
          autoComplete="off"
          aria-label={t("projects.switchSearch")}
        />
      </label>

      {/* The rows scroll, the field above them and the link below them do not.
          A dozen two-line projects are taller than the window, and `.crm-menu`
          only knows how to cap a menu at the screen - which is how this panel
          came to start at the top edge of the page and end at the bottom one
          instead of hanging under the title it opens from. */}
      <div className="crm-switch-rows">
        {rows === null ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : null}

        {rows !== null && shown.length === 0 ? (
          <div className="px-4 py-4 text-center text-[12.75px] text-muted-foreground">
            {t(failed ? "projects.switchFailed" : "projects.switchEmpty")}
          </div>
        ) : null}

        {shown.map((p) => (
          <button key={p.id} type="button" className="crm-mi" onClick={() => go(p.id)}>
            {/* The mark explains the order. Without it the pinned rows look like
                the newest ones, and the list looks sorted by nothing. */}
            {p.pinned ? <Pin className="size-4 shrink-0" /> : null}
            <span className="min-w-0 flex-1">
              <span className="crm-trunc block">{p.name}</span>
              {p.client?.name ? <span className="crm-mi-sub crm-trunc block">{p.client.name}</span> : null}
            </span>
          </button>
        ))}
      </div>

      <div className="crm-menu-sep" />
      {/* The way out of a shortlist. The menu shows a page of the register, so
          it can be the wrong page - and a dead end there would send the
          operator back through the browser's Back button. */}
      <Link to="/projects" className="crm-mi" onClick={close}>
        {t("projects.switchAll")}
      </Link>
    </>
  );
}
